import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Sparkles, Loader2, X } from "lucide-react";

const FREE_FEATURES = [
  { label: "Search remote jobs worldwide", included: true },
  { label: "Free Match Score on every job", included: true, hook: true },
  { label: "Keyword gap analysis with placement hints", included: true, hook: true },
  { label: "Success stories & career playbooks", included: true, hook: true },
  { label: "Save up to 20 applications in the tracker", included: true },
  { label: "AI resume + cover letter tailoring", included: false },
  { label: "10+ industry PDF templates", included: false },
  { label: "Company Insights + Glassdoor / LinkedIn intel", included: false },
  { label: "Interview prep + Thank-You letter generator", included: false },
  { label: "Auto tracker: interview reminders & 7-day nudges", included: false },
];

const PRO_FEATURES = [
  { label: "Everything in Free", strong: true },
  { label: "The Hub — unlimited AI resume + cover letter tailoring, with company-value alignment" },
  { label: "The Design — 10+ industry PDF templates: Modern, Tech, Finance, Creative, Healthcare, Skilled Trades, Minimalist, Academic, Executive, Student" },
  { label: "The Tracker — auto-saving from Career AI Hub, manual entries, private interview notes, 7-day follow-up nudges, interview email reminders" },
  { label: "The Intel — deep company culture snapshots + Glassdoor & LinkedIn quick links" },
  { label: "The Prep — predicted interview questions, STAR stories, and an AI Thank-You letter generator from your notes" },
  { label: "Priority Claude Sonnet AI compute" },
  { label: "Cancel anytime — no auto-renew surprises" },
];

export default function Pricing() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);

  const checkout = async () => {
    if (!user) { toast.error("Please log in first"); return; }
    if (!tosAccepted) { toast.error("Please accept the Service Agreement to continue"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { tos_accepted: true, tos_version: "2026-02-11" });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
      <div className="text-center mb-10">
        <div className="label-eyebrow">Pricing</div>
        <h1 className="text-5xl md:text-6xl font-black tracking-tighter">One plan. Everything unlocked.</h1>
        <p className="mt-3 text-gray-600 max-w-2xl mx-auto">
          Start free with the Match Score and keyword gap analysis. When you're ready to actually land the offer, unlock the full AI kit for <strong>$19 / month</strong>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        <div className="nb-card p-8">
          <div className="label-eyebrow">Free — The Hook</div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-5xl font-black tracking-tighter">$0</span>
            <span className="text-gray-500 text-sm">/forever</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm">
            {FREE_FEATURES.map((f, i) => (
              <li key={i} className={`flex items-start gap-2 ${!f.included ? "text-gray-400 line-through" : ""}`}>
                {f.included ? <Check className="w-4 h-4 text-[#22c55e] mt-0.5" strokeWidth={3} /> : <X className="w-4 h-4 text-gray-300 mt-0.5" />}
                <span>{f.label}{f.hook && <span className="ml-1.5 nb-pill bg-[#eab308] text-black text-[9px] align-middle">HOOK</span>}</span>
              </li>
            ))}
          </ul>
          <Link to="/signup" data-testid="pricing-free-cta" className="nb-btn bg-white w-full py-3 mt-8 inline-flex items-center justify-center">Start free</Link>
        </div>

        <div className="nb-card p-8 relative bg-[#4338ca] text-white">
          <div className="absolute -top-4 -right-4 nb-pill bg-[#eab308] text-black text-sm">Everything · Unlimited</div>
          <div className="label-eyebrow text-white/70">Pro</div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-6xl font-black tracking-tighter">$19</span>
            <span className="text-white/70 text-sm">/month</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm">
            {PRO_FEATURES.map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#eab308] mt-0.5" strokeWidth={3} />
                <span className={f.strong ? "font-bold" : ""}>{f.label}</span>
              </li>
            ))}
          </ul>

          <label className="mt-6 flex items-start gap-2 text-xs cursor-pointer select-none" htmlFor="tos-checkbox">
            <input
              id="tos-checkbox"
              data-testid="tos-checkbox"
              type="checkbox"
              checked={tosAccepted}
              onChange={(e) => setTosAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#eab308]"
            />
            <span className="text-white/90">
              I agree to the{" "}
              <Link to="/terms" target="_blank" data-testid="tos-link" className="underline font-bold">
                Service Agreement & Terms of Service
              </Link>{" "}
              (v2026-02-11). I understand each payment grants 30 days of Pro access and is non-refundable once AI features are used.
            </span>
          </label>

          <button
            data-testid="pricing-pro-cta"
            onClick={checkout}
            disabled={busy || !tosAccepted}
            className="nb-btn bg-[#eab308] text-black w-full py-3 mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><Sparkles className="w-4 h-4" /> Upgrade to Pro — $19/mo</>}
          </button>
          <p className="mt-3 text-center text-xs text-white/70">Secure checkout via Stripe. Test card 4242 4242 4242 4242.</p>
        </div>
      </div>

      <div className="mt-10 text-center text-sm text-gray-600">
        Questions? Read the full{" "}
        <Link to="/terms" className="underline font-bold">Service Agreement</Link>.
      </div>
    </div>
  );
}
